import { Controller } from "@hotwired/stimulus"

// Polls /admin/gridsignal/sources/extraction_run_status.json every
// 2s while an ExtractionRun is active. Mirrors the BulkGeocode /
// FullIngest monitor controllers.
export default class GsExtractionRunMonitorController extends Controller {
  static targets = [
    "statusBadge", "progressBar",
    "totalDocsPending", "docsProcessed",
    "totalChunks", "chunksProcessed",
    "signalsAdded", "chunksNotRelevant", "chunksFailed",
    "elapsedText", "etaText", "currentStep",
    "stageLabel",
    "errorBlock", "errorText"
  ]
  static values = { url: String, active: Boolean }

  connect() {
    if (this.activeValue) this.poll()
  }

  disconnect() {
    if (this._timer) clearTimeout(this._timer)
  }

  poll() {
    fetch(this.urlValue, { headers: { "Accept": "application/json" } })
      .then(r => r.json())
      .then(data => this.update(data))
      .catch(() => {})
      .finally(() => {
        if (this.activeValue) {
          this._timer = setTimeout(() => this.poll(), 2000)
        }
      })
  }

  update(data) {
    if (data.status === "none") return

    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.textContent = this.capitalize(data.status)
      this.statusBadgeTarget.className = `status-badge status-${data.status}`
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${data.progress_percentage}%`
    }

    if (this.hasTotalDocsPendingTarget)  this.totalDocsPendingTarget.textContent  = this.formatNumber(data.total_docs_pending)
    if (this.hasDocsProcessedTarget)     this.docsProcessedTarget.textContent     = this.formatNumber(data.docs_processed)
    if (this.hasStageLabelTarget && data.stage_label) this.stageLabelTarget.textContent = data.stage_label
    if (this.hasTotalChunksTarget)       this.totalChunksTarget.textContent       = this.formatNumber(data.total_chunks)
    if (this.hasChunksProcessedTarget)   this.chunksProcessedTarget.textContent   = this.formatNumber(data.chunks_processed)
    if (this.hasSignalsAddedTarget)      this.signalsAddedTarget.textContent      = this.formatNumber(data.signals_added)
    if (this.hasChunksNotRelevantTarget) this.chunksNotRelevantTarget.textContent = this.formatNumber(data.chunks_not_relevant)
    if (this.hasChunksFailedTarget)      this.chunksFailedTarget.textContent      = this.formatNumber(data.chunks_failed)

    if (this.hasCurrentStepTarget && data.current_step) {
      this.currentStepTarget.textContent = data.current_step
    }

    if (this.hasElapsedTextTarget && data.elapsed_seconds != null) {
      this.elapsedTextTarget.textContent = this.formatDuration(data.elapsed_seconds)
    }

    if (this.hasEtaTextTarget) {
      if (data.status === "completed") {
        this.etaTextTarget.textContent = "Done"
      } else if (data.eta_seconds != null && data.eta_seconds > 0) {
        this.etaTextTarget.textContent = `~${this.formatDuration(data.eta_seconds)} remaining`
      } else {
        this.etaTextTarget.textContent = "Calculating…"
      }
    }

    if (data.status === "failed" && data.error_log) {
      if (this.hasErrorBlockTarget) this.errorBlockTarget.hidden = false
      if (this.hasErrorTextTarget)  this.errorTextTarget.textContent = data.error_log
    }

    if (data.status === "completed" || data.status === "failed") {
      this.activeValue = false
      // Reload once so the final counts render alongside the main
      // stats row (Signals count, etc.) which doesn't live-update.
      setTimeout(() => window.location.reload(), 800)
    }
  }

  capitalize(str) {
    if (!str) return ""
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  formatNumber(n) {
    if (n == null) return "0"
    return Number(n).toLocaleString()
  }

  formatDuration(totalSeconds) {
    if (totalSeconds == null) return "--"
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.floor((totalSeconds % 3600) / 60)
    const s = Math.floor(totalSeconds % 60)
    if (h > 0) return `${h}h ${m}m ${s}s`
    if (m > 0) return `${m}m ${s}s`
    return `${s}s`
  }
}
